import { PublicNav, PublicFooter } from "../components/PublicLayout";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <PublicNav />
      <div className="max-w-4xl mx-auto px-5 pt-28 pb-16">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: March 2, 2026</p>
        <div className="prose prose-slate prose-sm max-w-none space-y-6 text-[14px] leading-relaxed text-slate-600">

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>1. Introduction</h2>
            <p>Zest AI ("Zest," "we," "us," or "our") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information in compliance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable Canadian privacy laws.</p>
            <p>This policy applies to all users of our AI-powered restaurant phone ordering platform, including restaurant owners, their staff, and callers who interact with our AI phone agent.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>2. Information We Collect</h2>
            <p>We collect the following categories of personal information:</p>
            <h3 className="text-base font-semibold text-slate-700">2.1 Account Information</h3>
            <ul className="list-disc pl-5 space-y-1"><li>Name, email address, and password (encrypted)</li><li>Restaurant business name, address, and phone numbers</li><li>Payment and billing information (processed via third-party payment processors)</li></ul>
            <h3 className="text-base font-semibold text-slate-700">2.2 Call Data</h3>
            <ul className="list-disc pl-5 space-y-1"><li>Caller phone numbers</li><li>Call recordings and transcripts</li><li>Order details (items, prices, customer names)</li><li>Call duration, timestamps, and outcomes</li></ul>
            <h3 className="text-base font-semibold text-slate-700">2.3 Usage Data</h3>
            <ul className="list-disc pl-5 space-y-1"><li>Dashboard access logs and activity</li><li>Feature usage patterns</li><li>Device and browser information</li></ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Service Delivery:</strong> To operate our AI phone agent, process orders, and send order confirmations via SMS</li>
              <li><strong>Account Management:</strong> To create and manage your account, process payments, and provide customer support</li>
              <li><strong>Service Improvement:</strong> To analyze call data and improve our AI's accuracy and natural language capabilities</li>
              <li><strong>Communication:</strong> To send service-related notifications, updates, and support responses</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and respond to legal requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>4. Consent</h2>
            <p>We obtain your consent before collecting, using, or disclosing your personal information. By creating an account and using our services, you consent to the collection and use of your information as described in this policy.</p>
            <p>For call recordings: Callers are informed at the beginning of each call that the conversation may be recorded for quality and training purposes. Continued participation in the call constitutes implied consent.</p>
            <p>You may withdraw your consent at any time by contacting us at <a href="mailto:hello@usezest.ca" className="text-primary hover:underline">hello@usezest.ca</a>, subject to legal or contractual restrictions.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>5. Disclosure of Information</h2>
            <p>We may share your personal information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>POS System Providers:</strong> To create and manage orders in your restaurant's point-of-sale system</li>
              <li><strong>Telephony Provider:</strong> For call handling and SMS delivery</li>
              <li><strong>AI Processing:</strong> For natural language processing of call conversations (anonymized where possible)</li>
              <li><strong>Payment Processors:</strong> For billing and subscription management</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or court order</li>
            </ul>
            <p>We require all third-party service providers to maintain appropriate safeguards for the protection of your personal information.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>6. Data Retention</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Account data: For the duration of your account plus 30 days after deletion</li>
              <li>Call recordings: Up to 90 days, unless required for dispute resolution</li>
              <li>Call transcripts and order data: For the duration of your subscription</li>
              <li>Billing records: As required by applicable tax and financial regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>7. Security Safeguards</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Access controls and role-based permissions</li>
              <li>Regular security assessments</li>
              <li>Secure hosting infrastructure with monitoring</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>8. Your Rights Under PIPEDA</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access</strong> your personal information held by us</li>
              <li><strong>Correct</strong> any inaccurate or incomplete information</li>
              <li><strong>Withdraw consent</strong> for future collection, use, or disclosure</li>
              <li><strong>Challenge compliance</strong> with this policy</li>
              <li><strong>File a complaint</strong> with the Office of the Privacy Commissioner of Canada</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href="mailto:hello@usezest.ca" className="text-primary hover:underline">hello@usezest.ca</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>9. Breach Notification</h2>
            <p>In the event of a data breach that creates a real risk of significant harm, we will notify affected individuals and the Office of the Privacy Commissioner of Canada as soon as feasible, in accordance with PIPEDA requirements.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our website and, where appropriate, by email. Your continued use of our services after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>11. Contact Us</h2>
            <p><strong>Zest AI</strong><br />Email: <a href="mailto:hello@usezest.ca" className="text-primary hover:underline">hello@usezest.ca</a><br />Website: <a href="https://usezest.ca" className="text-primary hover:underline">usezest.ca</a></p>
          </section>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
